/**
 * Rollback mock data for local development and UI mockup work.
 * Consumed by rollbackMigrationService.ts when USE_ROLLBACK_MOCK_DATA is true.
 */

import type { Migration } from '../types/migration';
import type { RollbackPlanResponse, RollbackQuickPreview } from '../types/rollback';

// ---------------------------------------------------------------------------
// Timestamp helpers (dynamic so age indicators stay correct whenever the demo runs)
// ---------------------------------------------------------------------------

const _now = Date.now();
const _hoursAgo = (h: number): string => new Date(_now - h * 60 * 60 * 1000).toISOString();
const _daysAgo = (d: number): string => new Date(_now - d * 24 * 60 * 60 * 1000).toISOString();

// ---------------------------------------------------------------------------
// Mock migrations
// ---------------------------------------------------------------------------

/**
 * Three completed migrations covering the demo age-indicator scenarios:
 *
 *   mock-rollback-2h   — green age  (~2 h)
 *   mock-rollback-3d   — amber age  (3 days)
 *   mock-rollback-12d  — red age    (12 days)
 */
export const MOCK_ROLLBACK_MIGRATIONS: Migration[] = [
  {
    id: 'mock-rollback-2h',
    correlationId: 'corr-2h',
    name: 'Mock — Full Migration (2 h ago)',
    customerId: 'customer-acme',
    createdBy: 'admin@acme.example.com',
    sourceOrgId: 'org-src-001',
    sourceRegion: 'us-east-1',
    targetOrgId: 'org-tgt-001',
    targetRegion: 'us-east-1',
    sourceOrgName: 'ACME Production',
    targetOrgName: 'ACME UAT',
    resources: ['genesyscloud_flow', 'genesyscloud_routing_queue', 'genesyscloud_auth_role'],
    exportAll: false,
    includeDependencies: true,
    isSnapshot: false,
    isActive: false,
    status: 'RollbackPlanAwaitingApproval',
    createdAtUtc: _hoursAgo(3),
    updatedAtUtc: _hoursAgo(2),
    job1Export: {
      status: 'Complete', artifactCount: 47,
      blobPath: 'customers/customer-acme/migrations/mock-rollback-2h/export',
      startedAtUtc: _hoursAgo(3), completedAtUtc: _hoursAgo(2.5), totalBytes: 284720, success: true
    },
    job2Transform: {
      status: 'Complete', artifactCount: 47,
      blobPath: 'customers/customer-acme/migrations/mock-rollback-2h/transform',
      startedAtUtc: _hoursAgo(2.5), completedAtUtc: _hoursAgo(2.3), success: true
    },
    job3Import: {
      status: 'Complete', artifactCount: 47,
      blobPath: 'customers/customer-acme/migrations/mock-rollback-2h/import',
      startedAtUtc: _hoursAgo(2.3), completedAtUtc: _hoursAgo(2), success: true
    }
  },
  {
    id: 'mock-rollback-3d',
    correlationId: 'corr-3d',
    name: 'Mock — Queue Refresh (3 days ago)',
    customerId: 'customer-acme',
    createdBy: 'ops@acme.example.com',
    sourceOrgId: 'org-src-001',
    sourceRegion: 'us-east-1',
    targetOrgId: 'org-tgt-002',
    targetRegion: 'eu-central-1',
    sourceOrgName: 'ACME Production',
    targetOrgName: 'ACME EU',
    resources: ['genesyscloud_routing_queue'],
    exportAll: false,
    includeDependencies: false,
    isSnapshot: false,
    isActive: false,
    status: 'RollbackPlanAwaitingApproval',
    createdAtUtc: _daysAgo(3.2),
    updatedAtUtc: _daysAgo(3),
    job1Export: {
      status: 'Complete', artifactCount: 18,
      blobPath: 'customers/customer-acme/migrations/mock-rollback-3d/export',
      startedAtUtc: _daysAgo(3.2), completedAtUtc: _daysAgo(3.1), totalBytes: 62100, success: true
    },
    job2Transform: {
      status: 'Complete', artifactCount: 18,
      blobPath: 'customers/customer-acme/migrations/mock-rollback-3d/transform',
      startedAtUtc: _daysAgo(3.1), completedAtUtc: _daysAgo(3.05), success: true
    },
    job3Import: {
      status: 'Complete', artifactCount: 18,
      blobPath: 'customers/customer-acme/migrations/mock-rollback-3d/import',
      startedAtUtc: _daysAgo(3.05), completedAtUtc: _daysAgo(3), success: true
    }
  },
  {
    id: 'mock-rollback-12d',
    correlationId: 'corr-12d',
    name: 'Mock — Skills Migration (12 days ago)',
    customerId: 'customer-acme',
    createdBy: 'admin@acme.example.com',
    sourceOrgId: 'org-src-001',
    sourceRegion: 'us-east-1',
    targetOrgId: 'org-tgt-001',
    targetRegion: 'us-east-1',
    sourceOrgName: 'ACME Production',
    targetOrgName: 'ACME UAT',
    resources: ['genesyscloud_routing_skill', 'genesyscloud_routing_queue'],
    exportAll: false,
    includeDependencies: false,
    isSnapshot: false,
    isActive: false,
    status: 'RollbackPlanAwaitingApproval',
    createdAtUtc: _daysAgo(12.2),
    updatedAtUtc: _daysAgo(12),
    job1Export: {
      status: 'Complete', artifactCount: 31,
      blobPath: 'customers/customer-acme/migrations/mock-rollback-12d/export',
      startedAtUtc: _daysAgo(12.2), completedAtUtc: _daysAgo(12.1), totalBytes: 118400, success: true
    },
    job2Transform: {
      status: 'Complete', artifactCount: 31,
      blobPath: 'customers/customer-acme/migrations/mock-rollback-12d/transform',
      startedAtUtc: _daysAgo(12.1), completedAtUtc: _daysAgo(12.05), success: true
    },
    job3Import: {
      status: 'Complete', artifactCount: 31,
      blobPath: 'customers/customer-acme/migrations/mock-rollback-12d/import',
      startedAtUtc: _daysAgo(12.05), completedAtUtc: _daysAgo(12), success: true
    }
  },
  {
    id: 'mock-completed-1d',
    correlationId: 'corr-completed-1d',
    name: 'Mock — Queue Migration (1 day ago)',
    customerId: 'customer-acme',
    createdBy: 'admin@acme.example.com',
    sourceOrgId: 'org-src-001',
    sourceRegion: 'us-east-1',
    targetOrgId: 'org-tgt-001',
    targetRegion: 'us-east-1',
    sourceOrgName: 'ACME Production',
    targetOrgName: 'ACME UAT',
    resources: ['genesyscloud_routing_queue', 'genesyscloud_user'],
    exportAll: false,
    includeDependencies: true,
    isSnapshot: false,
    isActive: false,
    status: 'completed',
    createdAtUtc: _daysAgo(1.2),
    updatedAtUtc: _daysAgo(1),
    job1Export: {
      status: 'Complete', artifactCount: 18,
      blobPath: 'customers/customer-acme/migrations/mock-completed-1d/export',
      startedAtUtc: _daysAgo(1.2), completedAtUtc: _daysAgo(1.15), totalBytes: 72800, success: true
    },
    job2Transform: {
      status: 'Complete', artifactCount: 18,
      blobPath: 'customers/customer-acme/migrations/mock-completed-1d/transform',
      startedAtUtc: _daysAgo(1.15), completedAtUtc: _daysAgo(1.1), success: true
    },
    job3Import: {
      status: 'Complete', artifactCount: 18,
      blobPath: 'customers/customer-acme/migrations/mock-completed-1d/import',
      startedAtUtc: _daysAgo(1.1), completedAtUtc: _daysAgo(1), success: true
    }
  },
];

// ---------------------------------------------------------------------------
// Mock rollback plan
// ---------------------------------------------------------------------------

/**
 * Builds a realistic RollbackPlanResponse for the given migrationId.
 *
 * Includes:
 * - 12 delete actions across genesyscloud_flow resources
 * - 5 revert actions across genesyscloud_routing_queue resources with property-level changedAttributes
 * - 2 restore actions across genesyscloud_auth_role resources
 */
export function MOCK_ROLLBACK_PLAN(migrationId: string): RollbackPlanResponse {
  return {
    migrationId,
    planGeneratedAtUtc: '2026-05-21T10:00:00Z',
    totalResources: 19,
    resourcesToDelete: 12,
    resourcesToRevert: 5,
    resourcesToRestore: 2,
    resources: [
      // -----------------------------------------------------------------------
      // 12 x genesyscloud_flow  →  delete (created by migration)
      // -----------------------------------------------------------------------
      {
        resourceType: 'genesyscloud_flow',
        resourceName: 'main_ivr_flow',
        resourceAddress: 'genesyscloud_flow.main_ivr_flow',
        action: 'delete'
      },
      {
        resourceType: 'genesyscloud_flow',
        resourceName: 'after_hours_flow',
        resourceAddress: 'genesyscloud_flow.after_hours_flow',
        action: 'delete'
      },
      {
        resourceType: 'genesyscloud_flow',
        resourceName: 'holiday_flow',
        resourceAddress: 'genesyscloud_flow.holiday_flow',
        action: 'delete'
      },
      {
        resourceType: 'genesyscloud_flow',
        resourceName: 'escalation_flow',
        resourceAddress: 'genesyscloud_flow.escalation_flow',
        action: 'delete'
      },
      {
        resourceType: 'genesyscloud_flow',
        resourceName: 'voicemail_flow',
        resourceAddress: 'genesyscloud_flow.voicemail_flow',
        action: 'delete'
      },
      {
        resourceType: 'genesyscloud_flow',
        resourceName: 'callback_flow',
        resourceAddress: 'genesyscloud_flow.callback_flow',
        action: 'delete'
      },
      {
        resourceType: 'genesyscloud_flow',
        resourceName: 'survey_flow',
        resourceAddress: 'genesyscloud_flow.survey_flow',
        action: 'delete'
      },
      {
        resourceType: 'genesyscloud_flow',
        resourceName: 'transfer_flow',
        resourceAddress: 'genesyscloud_flow.transfer_flow',
        action: 'delete'
      },
      {
        resourceType: 'genesyscloud_flow',
        resourceName: 'overflow_flow',
        resourceAddress: 'genesyscloud_flow.overflow_flow',
        action: 'delete'
      },
      {
        resourceType: 'genesyscloud_flow',
        resourceName: 'chat_flow',
        resourceAddress: 'genesyscloud_flow.chat_flow',
        action: 'delete'
      },
      {
        resourceType: 'genesyscloud_flow',
        resourceName: 'email_flow',
        resourceAddress: 'genesyscloud_flow.email_flow',
        action: 'delete'
      },
      {
        resourceType: 'genesyscloud_flow',
        resourceName: 'sms_flow',
        resourceAddress: 'genesyscloud_flow.sms_flow',
        action: 'delete'
      },
      // -----------------------------------------------------------------------
      // 5 x genesyscloud_routing_queue  →  revert (existed before, properties changed)
      // -----------------------------------------------------------------------
      {
        resourceType: 'genesyscloud_routing_queue',
        resourceName: 'support_tier1',
        resourceAddress: 'genesyscloud_routing_queue.support_tier1',
        action: 'revert',
        changedAttributes: [
          {
            attributeName: 'skill_evaluation_method',
            currentValue: 'BEST',
            originalValue: 'ALL'
          },
          {
            attributeName: 'acw_wrapup_prompt',
            currentValue: 'OPTIONAL',
            originalValue: 'MANDATORY_TIMEOUT'
          }
        ]
      },
      {
        resourceType: 'genesyscloud_routing_queue',
        resourceName: 'support_tier2',
        resourceAddress: 'genesyscloud_routing_queue.support_tier2',
        action: 'revert',
        changedAttributes: [
          {
            attributeName: 'default_script_id',
            currentValue: 'script-uat-99a1b2c3',
            originalValue: 'script-prod-44f5e6a7'
          },
          {
            attributeName: 'queue_flow_id',
            currentValue: null,
            originalValue: 'flow-prod-b8c9d0e1'
          }
        ]
      },
      {
        resourceType: 'genesyscloud_routing_queue',
        resourceName: 'billing_general',
        resourceAddress: 'genesyscloud_routing_queue.billing_general',
        action: 'revert',
        changedAttributes: [
          {
            attributeName: 'auto_answer_only',
            currentValue: 'true',
            originalValue: 'false'
          }
        ]
      },
      {
        resourceType: 'genesyscloud_routing_queue',
        resourceName: 'sales_inbound',
        resourceAddress: 'genesyscloud_routing_queue.sales_inbound',
        action: 'revert',
        changedAttributes: [
          {
            attributeName: 'skill_evaluation_method',
            currentValue: 'BEST',
            originalValue: 'COMPETENCY'
          },
          {
            attributeName: 'calling_party_name',
            currentValue: 'ACME UAT Sales',
            originalValue: 'ACME Sales'
          },
          {
            attributeName: 'calling_party_number',
            currentValue: '+15550001111',
            originalValue: '+15559998888'
          }
        ]
      },
      {
        resourceType: 'genesyscloud_routing_queue',
        resourceName: 'retention_outbound',
        resourceAddress: 'genesyscloud_routing_queue.retention_outbound',
        action: 'revert',
        changedAttributes: [
          {
            attributeName: 'outbound_messaging_sms_address_id',
            currentValue: null,
            originalValue: 'sms-addr-prod-c2d3e4f5'
          }
        ]
      },
      // -----------------------------------------------------------------------
      // 2 x genesyscloud_auth_role  →  restore (deleted by migration, need re-creating)
      // -----------------------------------------------------------------------
      {
        resourceType: 'genesyscloud_auth_role',
        resourceName: 'legacy_supervisor_role',
        resourceAddress: 'genesyscloud_auth_role.legacy_supervisor_role',
        action: 'restore'
      },
      {
        resourceType: 'genesyscloud_auth_role',
        resourceName: 'read_only_auditor',
        resourceAddress: 'genesyscloud_auth_role.read_only_auditor',
        action: 'restore'
      }
    ]
  };
}

// ---------------------------------------------------------------------------
// Quick-preview mock (keyed per migrationId, falls back to a generic preview)
// ---------------------------------------------------------------------------

const PREVIEW_OVERRIDES: Partial<Record<string, RollbackQuickPreview>> = {};

/**
 * Returns a RollbackQuickPreview for the given migrationId.
 * Uses a per-migration override if available; otherwise derives from MOCK_ROLLBACK_PLAN.
 */
export function MOCK_ROLLBACK_QUICK_PREVIEW(migrationId: string): RollbackQuickPreview {
  const override = PREVIEW_OVERRIDES[migrationId];
  if (override) return override;

  const plan = MOCK_ROLLBACK_PLAN(migrationId);
  const migration = MOCK_ROLLBACK_MIGRATIONS.find((m) => m.id === migrationId);

  return {
    migrationId,
    migrationName: migration?.name ?? 'Unknown Migration',
    estimatedResourcesToDelete: plan.resourcesToDelete,
    estimatedResourcesToRevert: plan.resourcesToRevert,
    estimatedResourcesToRestore: plan.resourcesToRestore,
    snapshotAvailable: true,
    snapshotCapturedAtUtc: '2026-04-10T08:00:00Z',
    resourceBreakdown: plan.resources,
  };
}
